<template>
  <app-page-container>
    <app-spinner v-if="!is_page_loaded"/>
    <template v-else>
      <app-cells :indent="false" class="cells--start-vertical">
        <div class="safari-itineraries"
             :style="{ pointerEvents: groupDetail.reservation.itinerary_completed ? 'none' : 'inherit' }">
          <div v-for="(item, index) in safariItineraries" :key="index" class="safari-itineraries__item">
            <div class="safari-itineraries__header">
              <div class="safari-itineraries__day">
                Day {{ index + 1 }} | {{ formatDateMonth(item.date) }} |
                {{ item.type.name }}
                <template v-if="item.national_park">| {{ item.national_park.name }}</template>
                <template v-if="item.national_park_area">({{ item.national_park_area.name }})</template>
              </div>
              <app-button
                v-if="!itinerary_readonly && item.national_park && item.type.code === 'safari' && !groupDetail.reservation.itinerary_completed"
                @click.native="onClickBrowseTemplates(item, index, `browse_templates_${index}`)"
                size="small"
                :ref="`browse_templates_${index}`"
              >
                Browse templates
              </app-button>
            </div>
            <template v-if="item.type.code !== 'empty_run'">
              <div v-if="index === 0" class="safari-itineraries__place">
                <template v-if="item.pick_up && item.pick_up.length > 0">
                  <template v-if="item.pick_up[0].reservation_status.code === 'booked_provisionally'">
                    Pick up: <span style="color: #439ad1">{{ getHotelsName(item.pick_up) }} (Provisional)</span>
                  </template>
                  <template v-else>
                    Pick up: <span>{{ getHotelsName(item.pick_up) }}</span>
                  </template>
                </template>
                <template v-else>Pick up: <span class="status-tag"
                                                style="background-color: red">Reservation not found</span></template>
              </div>
              <div class="safari-itineraries__timings">
                <div
                  v-for="(timing, tIndex) in item.timings"
                  :key="tIndex"
                  class="safari-itineraries__timing"
                  :style="{ backgroundColor: !timing.fake ? '' : timing.reservation_status && (timing.reservation_status === 'booked_provisionally' || timing.reservation_status === 'new') ? '#e7f6ff' : '#efffe4' }"
                >
                  <input
                    @blur="sortTimings(index)"
                    v-model="timing.time"
                    :id="`timing_${index}`"
                    :name="`timing_${index}`"
                    v-mask="'99:99'"
                    type="text"
                    placeholder="__:__"
                    class="safari-itineraries__time"
                    autocomplete="off"
                    :readonly="timing.fake || itinerary_readonly"
                  />
                  <button
                    v-if="!itinerary_readonly"
                    @click="removeTiming(index, tIndex)"
                    type="button"
                    class="safari-itineraries__remove-time"
                    :class="{ 'safari-itineraries__remove-time--hide': timing.fake }"
                    title="Remove timing"
                  >
                    ✕
                  </button>
                  <textarea
                    @input="autoResize($event)"
                    v-model="timing.description"
                    rows="1"
                    placeholder="Write the text here"
                    class="safari-itineraries__description"
                    ref="autoResizeTextarea"
                    :readonly="timing.fake || itinerary_readonly"
                  />
                </div>
                <button
                  v-if="!itinerary_readonly && !groupDetail.reservation.itinerary_completed"
                  @click="addTiming(index)"
                  type="button"
                  title="Add timing"
                  class="safari-itineraries__add-time"
                >
                  <img src="@/assets/img/plus-green-icon.svg" alt="add time">
                </button>
              </div>
              <div class="safari-itineraries__place">
                <template v-if="item.drop_off && item.drop_off.length > 0">
                  <template v-if="item.drop_off[0].reservation_status.code === 'booked_provisionally'">
                    Drop off: <span style="color: #439ad1">{{ getHotelsName(item.drop_off) }} (Provisional)</span>
                  </template>
                  <template v-else>
                    Drop off: <span>{{ getHotelsName(item.drop_off) }}</span>
                  </template>
                </template>
                <template v-else>
                  <template v-if="!groupDetail.reservation.itinerary_completed">
                    Drop off: <span style="color: red">Reservation not found</span>
                  </template>
                  <template v-else>
                    Drop off: <span style="color: #439ad1">Self arrangements</span>
                  </template>
                </template>
              </div>
            </template>
            <div
              v-for="warning in (item.meal_warnings || [])"
              :key="warning.entry_key"
              class="safari-itineraries__meal-warning"
              role="note"
            >
              {{ warning.description }}
            </div>
            <textarea
              v-if="(!groupDetail.reservation.itinerary_completed) || (groupDetail.reservation.itinerary_completed && item.notes)"
              @input="autoResize($event)"
              v-model="item.notes"
              :id="`note_day_${index + 1}`"
              rows="1"
              placeholder="Write notes for this day here"
              :readonly="itinerary_readonly"
              class="safari-itineraries__note"
              ref="autoResizeNote"
            />
          </div>
        </div>
        <app-button
          v-if="!itinerary_readonly && !groupDetail.reservation.itinerary_completed"
          @click.native="onSaveItineraryClick"
          ref="save_itinerary"
          style="position: sticky; top: 10px; transform: rotate(90deg) translateY(75px) translateX(60px);"
        >
          Save Itinerary
        </app-button>
        <app-button
          v-if="groupDetail.reservation.itinerary_verified"
          @click.native="onExportItineraryClick"
          ref="export_itinerary"
          style="position: sticky; top: 10px; transform: rotate(90deg) translateY(90px) translateX(70px); min-width: 164px;"
          theme="warn"
        >
          Download Itinerary
        </app-button>
      </app-cells>
      <div class="downloads" style="margin-bottom: 15px;">
        <div class="downloads__item" :class="{'downloads__item--loading': itinerary_completed_loader}">
          <div class="downloads__left">
            <div class="downloads__info">Itinerary</div>
          </div>
          <div class="downloads__right">
            <div class="downloads__values">
              <span class="downloads__key" :title="itinerary_completed_readonly ? 'Contact Operations or a supervisor to reopen this itinerary.' : ''">Confirmed:</span>
              <app-switcher
                @change="onUpdateItineraryCompleted"
                v-model="groupDetail.reservation.itinerary_completed"
                :readonly="itinerary_readonly || itinerary_completed_readonly"
                :disabled="itinerary_readonly"
              />
            </div>
            <template
              v-if="groupDetail.reservation.itinerary_completed && groupDetail.reservation.itinerary_completed_by && !itinerary_completed_loader"
            >
              <div class="downloads__values">
                <span class="downloads__key">Completed at:</span>
                <span
                  class="downloads__value downloads__width">{{ dateTimeFormat(groupDetail.reservation.itinerary_completed_at)
                  }}</span>
              </div>
              <div class="downloads__values">
                <span class="downloads__key">Completed by:</span>
                <div class="downloads__person downloads__width">
                  <div class="downloads__photo">
                    <img :src="getUserAvatar(groupDetail.reservation.itinerary_completed_by?.crop.crop_url)"
                         alt="Person">
                  </div>
                  <span class="downloads__value">{{ groupDetail.reservation.itinerary_completed_by?.full_name || '-'
                    }}</span>
                </div>
              </div>
            </template>
          </div>
        </div>
      </div>
      <div v-if="itinerary_completed_saved || groupDetail.reservation.itinerary_verified" class="downloads">
        <div class="downloads__item" :class="{'downloads__item--loading': itinerary_verified_loader}">
          <div class="downloads__left">
            <div class="downloads__info">Itinerary</div>
          </div>
          <div class="downloads__right">
            <div class="downloads__values">
              <span class="downloads__key">Verified:</span>
              <app-switcher
                @change="onUpdateItineraryVerified"
                v-model="groupDetail.reservation.itinerary_verified"
                :readonly="itinerary_readonly || itinerary_verified_readonly"
                :disabled="itinerary_readonly"
              />
            </div>
            <template
              v-if="groupDetail.reservation.itinerary_verified && groupDetail.reservation.itinerary_completed_by && !itinerary_verified_loader"
            >
              <div class="downloads__values">
                <span class="downloads__key">Completed at:</span>
                <span
                  class="downloads__value downloads__width">{{ dateTimeFormat(groupDetail.reservation.itinerary_verified_at)
                  }}</span>
              </div>
              <div class="downloads__values">
                <span class="downloads__key">Completed by:</span>
                <div class="downloads__person downloads__width">
                  <div class="downloads__photo">
                    <img :src="getUserAvatar(groupDetail.reservation.itinerary_verified_by?.crop.crop_url)"
                         alt="Person">
                  </div>
                  <span class="downloads__value">{{ groupDetail.reservation.itinerary_verified_by?.full_name || '-'
                    }}</span>
                </div>
              </div>
            </template>
          </div>
        </div>
      </div>
      <modal
        name="browse-templates"
        :width="500"
        :adaptive="true"
        :clickToClose="false"
        :height="'auto'"
        :scrollable="true"
        class="modal-not-overflow"
        draggable=".popup__title"
      >
        <div class="popup">
          <button class="popup__close" type="button" @click="$modal.hide('browse-templates')"></button>
          <div class="popup__title">
            Day {{ current_day.index + 1 }} | {{ formatDateMonth(current_day.date) }} |
            {{ current_day.national_park?.name }}
          </div>
          <div class="popup__body">
            <app-form-field label="Template">
              <app-select
                v-model="template"
                :options="template_options"
                :clearable="false"
                placeholder="Choose a template"
              />
            </app-form-field>
            <app-cells-grid :columns="1" margin-top="20px" gap="8px">
              <div v-for="timing in template?.timings" :key="timing.id">
                <b>{{ timing.time }}:</b> {{ timing.description }}
              </div>
            </app-cells-grid>
            <app-cells :indent="false" position="center">
              <app-button @click.native="onTemplateImportClick" ref="template_import">Import</app-button>
            </app-cells>
          </div>
        </div>
      </modal>
    </template>
  </app-page-container>
</template>

<script>
import {
  getReservationsItineraryTimingTemplates,
  getReservationsSafariDriverItinerariesExport,
  getReservationsSafariItineraries,
  patchReservationSafari,
  patchReservationsSafariItineraries
} from '@/http'
import { format, parseISO } from 'date-fns'
import { mealItineraryTimings } from '@/utils/safari-meal-itinerary'

export default {
  name: 'ItineraryNew',
  props: {
    groupDetail: Object,
  },
  data() {
    return {
      is_page_loaded: false,
      safariItineraries: [],
      current_day: {},
      template: null,
      template_options: [],
      itinerary_completed_loader: false,
      itinerary_verified_loader: false,
      itinerary_completed_saved: Boolean(this.groupDetail.reservation.itinerary_completed),
    }
  },
  computed: {
    itinerary_readonly() {
      const roles = this.$store.state.userGroups.map(item => item.code)
      const editorRoles = ['general_manager', 'safari_supervisor', 'sales_manager', 'sales_supervisor', 'operations_supervisor']
      return roles.includes('operations_manager') && !roles.some(role => editorRoles.includes(role))
    },
    is_general_manager() {
      return this.$store.state.userGroups.map(item => item.code).includes('general_manager')
    },
    is_sales_supervisor() {
      return this.$store.state.userGroups.map(item => item.code).includes('sales_supervisor')
    },
    is_safari_supervisor() {
      return this.$store.state.userGroups.map(item => item.code).includes('general_manager') ||
        this.$store.state.userGroups.map(item => item.code).includes('safari_supervisor')
    },
    is_operations_supervisor() {
      return this.$store.state.userGroups.map(item => item.code).includes('operations_supervisor')
    },
    itinerary_completed_readonly() {
      const canReopen = this.is_general_manager || this.is_sales_supervisor || this.is_safari_supervisor || this.is_operations_supervisor
      return this.groupDetail.reservation.itinerary_verified || this.itinerary_completed_loader || this.itinerary_verified_loader || (this.itinerary_completed_saved && !canReopen)
    },
    itinerary_verified_readonly() {
      const lacksPermission = !this.is_general_manager &&
        !this.is_sales_supervisor &&
        !this.is_safari_supervisor &&
        !this.is_operations_supervisor
      const canOnlyRepairInvalidState = !this.itinerary_completed_saved &&
        this.groupDetail.reservation.itinerary_verified

      return lacksPermission ||
        this.itinerary_completed_loader ||
        this.itinerary_verified_loader ||
        (!this.itinerary_completed_saved && !canOnlyRepairInvalidState)
    },
  },
  watch: {
    'groupDetail.reservation.itinerary_completed'(value) {
      if (!this.itinerary_completed_loader) {
        this.itinerary_completed_saved = Boolean(value)
      }
    },
  },
  created() {
    getReservationsSafariItineraries(this.groupDetail.reservation.id)
      .then(res => {
        this.safariItineraries = res.data

        this.safariItineraries.forEach((item, index) => {
          if (item.flights.length > 0) {
            item.flights.forEach(flight => {
              let newTiming = {}

              if (flight.type === 'departure') {
                newTiming = {
                  time: flight.departure,
                  description: `Departure with ${ flight.airline.name }. ${ flight.origin.name } → ${ flight.destination.name }. Guests must be transferred to the airstrip 60 minutes before the flight time for check-in.`,
                  reservation_status: flight.reservation_status.code,
                  fake: true
                }
              } else if (flight.type === 'arrival') {
                newTiming = {
                  time: flight.arrival,
                  description: `Arrival with ${ flight.airline.name }. ${ flight.origin.name } → ${ flight.destination.name }. The recommended arrival time at the airstrip is 30 minutes before the flight's landing.`,
                  reservation_status: flight.reservation_status.code,
                  fake: true
                }
              } else if (flight.type === 'regular') {
                let regularTiming = {}

                newTiming = {
                  time: flight.departure,
                  description: `Departure with ${ flight.airline.name }. ${ flight.origin.name } → ${ flight.destination.name }. Guests must be transferred to the airstrip 60 minutes before the flight time for check-in.`,
                  reservation_status: flight.reservation_status.code,
                  fake: true
                }
                regularTiming = {
                  time: flight.arrival,
                  description: `Arrival with ${ flight.airline.name }. ${ flight.origin.name } → ${ flight.destination.name }. The recommended arrival time at the airstrip is 30 minutes before the flight's landing.`,
                  reservation_status: flight.reservation_status.code,
                  fake: true
                }
                item.timings.push(regularTiming)
              }
              item.timings.push(newTiming)
            })
          }

          item.timings.push(...mealItineraryTimings(item.meals))
          this.sortTimings(index)
        })

        setTimeout(() => {
          const textareas = this.$refs.autoResizeTextarea
          const notes = this.$refs.autoResizeNote
          const text = [...textareas || [], ...notes || []]

          if (text.length) {
            text.forEach(item => {
              this.autoResize({ target: item })
            })
          }
        }, 300)
        this.is_page_loaded = true
      })
  },
  methods: {
    onExportItineraryClick() {
      this.$refs.export_itinerary.preload = true
      getReservationsSafariDriverItinerariesExport(this.groupDetail.reservation.id)
        .finally(() => { this.$refs.export_itinerary.preload = false })
        .then(res => { window.open(res.data) })
    },
    onUpdateItineraryCompleted() {
      if (this.itinerary_readonly) return
      if (this.itinerary_completed_loader || this.itinerary_verified_loader) return

      this.itinerary_completed_loader = true
      if (this.groupDetail.reservation.itinerary_completed) {
        patchReservationsSafariItineraries(this.groupDetail.reservation.id, this.normalizeSafariItineraries())
          .then(() => {
            return patchReservationSafari(this.groupDetail.reservation.id, { itinerary_completed: true })
              .then(() => {
                this.itinerary_completed_saved = true
                this.$emit('getSafariInfo')
                this.$notify({
                  type: 'success',
                  title: 'Itinerary completed',
                })
              })
          })
          .catch(() => {
            this.groupDetail.reservation.itinerary_completed = this.itinerary_completed_saved
          })
          .finally(() => { this.itinerary_completed_loader = false })
      } else {
        patchReservationSafari(this.groupDetail.reservation.id, { itinerary_completed: false })
          .then(() => {
            this.itinerary_completed_saved = false
            this.$emit('getSafariInfo')
            this.$notify({
              type: 'error',
              title: 'Itinerary not completed',
            })
          })
          .catch(() => {
            this.groupDetail.reservation.itinerary_completed = this.itinerary_completed_saved
          })
          .finally(() => { this.itinerary_completed_loader = false })
      }
    },
    onUpdateItineraryVerified() {
      if (this.itinerary_readonly) return
      if (this.itinerary_verified_loader || this.itinerary_completed_loader) return
      if (!this.itinerary_completed_saved && this.groupDetail.reservation.itinerary_verified) {
        this.groupDetail.reservation.itinerary_verified = false
        return
      }

      this.itinerary_verified_loader = true
      if (this.groupDetail.reservation.itinerary_verified) {
        patchReservationSafari(this.groupDetail.reservation.id, { itinerary_verified: true })
          .then(() => {
            this.$emit('getSafariInfo')
            this.$notify({
              type: 'success',
              title: 'Itinerary verified',
            })
          })
          .catch(() => { this.groupDetail.reservation.itinerary_verified = false })
          .finally(() => { this.itinerary_verified_loader = false })
      } else {
        patchReservationSafari(this.groupDetail.reservation.id, { itinerary_verified: false })
          .then(() => {
            this.$emit('getSafariInfo')
            this.$notify({
              type: 'error',
              title: 'Itinerary not verified',
            })
          })
          .catch(() => { this.groupDetail.reservation.itinerary_verified = true })
          .finally(() => { this.itinerary_verified_loader = false })
      }
    },
    onTemplateImportClick() {
      if (this.itinerary_readonly) return
      const safari_day = this.safariItineraries[this.current_day.index]

      safari_day.timings = this.template.timings
        .map(item => (
          { time: item.time, description: item.description }
        ))

      if (safari_day.flights.length > 0) {
        safari_day.flights.forEach(flight => {
          let newTiming = {}

          if (flight.type === 'departure') {
            newTiming = {
              time: flight.departure,
              description: `Departure with ${ flight.airline.name }. ${ flight.origin.name } → ${ flight.destination.name }. Guests must be transferred to the airstrip 60 minutes before the flight time for check-in.`,
              reservation_status: flight.reservation_status.code,
              fake: true
            }
          } else if (flight.type === 'arrival') {
            newTiming = {
              time: flight.arrival,
              description: `Arrival with ${ flight.airline.name }. ${ flight.origin.name } → ${ flight.destination.name }. The recommended arrival time at the airstrip is 30 minutes before the flight's landing.`,
              reservation_status: flight.reservation_status.code,
              fake: true
            }
          } else if (flight.type === 'regular') {
            let regularTiming = {}

            newTiming = {
              time: flight.departure,
              description: `Departure with ${ flight.airline.name }. ${ flight.origin.name } → ${ flight.destination.name }. Guests must be transferred to the airstrip 60 minutes before the flight time for check-in.`,
              reservation_status: flight.reservation_status.code,
              fake: true
            }
            regularTiming = {
              time: flight.arrival,
              description: `Arrival with ${ flight.airline.name }. ${ flight.origin.name } → ${ flight.destination.name }. The recommended arrival time at the airstrip is 30 minutes before the flight's landing.`,
              reservation_status: flight.reservation_status.code,
              fake: true
            }
            safari_day.timings.push(regularTiming)
          }

          safari_day.timings.push(newTiming)
          this.sortCurrentTimings(safari_day)
        })
      }
      safari_day.timings.push(...mealItineraryTimings(safari_day.meals))
      this.sortCurrentTimings(safari_day)
      this.$modal.hide('browse-templates')

    },
    onClickBrowseTemplates(day, index, ref) {
      this.$refs[ref][0].preload = true
      this.current_day = day
      this.current_day.index = index
      getReservationsItineraryTimingTemplates({ national_park: day.national_park.code })
        .finally(() => { this.$refs[ref][0].preload = false })
        .then(res => {
          this.template_options = res.data
          this.template = this.template_options[0]
          this.$modal.show('browse-templates')
        })
    },
    getHotelsName(array) {
      return array.map(item => item.hotel.name).join(', ')
    },
    onSaveItineraryClick() {
      if (this.itinerary_readonly) return
      this.$refs.save_itinerary.preload = true
      patchReservationsSafariItineraries(this.groupDetail.reservation.id, this.normalizeSafariItineraries())
        .finally(() => { this.$refs.save_itinerary.preload = false })
        .then(() => {
          this.$notify({
            type: 'success',
            title: 'Itinerary saved successfully',
          })
        })
    },
    normalizeSafariItineraries() {
      let itineraries = []

      itineraries = this.safariItineraries.map(item => {
        return {
          id: item.id,
          notes: item.notes,
          timings: item.timings.filter(timing => !timing.fake && timing.time),
        }
      })

      return itineraries
    },
    addTiming(index) {
      if (this.itinerary_readonly) return
      this.safariItineraries[index].timings.push({ time: '', description: '' })
      this.$nextTick(() => {
        const textareas = this.$refs.autoResizeTextarea
        if (textareas && textareas.length) {
          this.autoResize({ target: textareas[textareas.length - 1] })
        }
      })
    },
    removeTiming(dayIndex, timingIndex) {
      if (this.itinerary_readonly) return
      this.safariItineraries[dayIndex].timings.splice(timingIndex, 1)
      this.$nextTick(() => {
        this.$refs.autoResizeTextarea.forEach(textarea => {
          this.autoResize({ target: textarea })
        })
      })
    },
    autoResize(event) {
      const textarea = event.target
      textarea.style.height = 'auto'
      textarea.style.height = `${ textarea.scrollHeight }px`
    },
    formatDateMonth(date) {
      if (!date) return ''
      return format(parseISO(date), 'dd MMMM')
    },
    dateTimeFormat(date) {
      return date ? format(parseISO(date), 'dd MMM yyyy HH:mm') : '-'
    },
    getUserAvatar(crop_url) {
      if (crop_url) return crop_url
      return require('@/assets/img/temp/avatar-slug.jpg')
    },
    sortTimings(dayIndex) {
      this.safariItineraries[dayIndex].timings.sort((a, b) => {
        if (!a.time) return 1
        if (!b.time) return -1

        const [aHours = 0, aMinutes = 0] = a.time.split(':').map(Number)
        const [bHours = 0, bMinutes = 0] = b.time.split(':').map(Number)

        return aHours - bHours || aMinutes - bMinutes
      })
    },
    sortCurrentTimings(day) {
      day.timings.sort((a, b) => {
        if (!a.time) return 1
        if (!b.time) return -1

        const [aHours = 0, aMinutes = 0] = a.time.split(':').map(Number)
        const [bHours = 0, bMinutes = 0] = b.time.split(':').map(Number)

        return aHours - bHours || aMinutes - bMinutes
      })
    }
  }
}
</script>

<style lang="sass" scoped>
.safari-itineraries
  width: 700px
  margin-bottom: 20px
  border-radius: 7px
  box-shadow: 2px 4px 8px rgba(0, 0, 0, 0.15)
  padding: 20px
  background-color: #f3f3f3
  z-index: 99
  overflow: hidden

.safari-itineraries__item
  margin-bottom: 15px
  border-radius: 7px
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.1)
  padding: 15px 25px 10px 15px
  background-color: #fff

  &:last-child
    margin-bottom: 0

.safari-itineraries__header
  display: flex
  justify-content: space-between
  align-items: flex-start
  gap: 15px
  margin-bottom: 10px

  button
    flex-shrink: 0

.safari-itineraries__day
  font-size: 20px
  font-weight: 800

.safari-itineraries__place
  margin-bottom: 10px

  span
    font-size: 15px
    font-weight: 800

.safari-itineraries__timings
  margin-bottom: 10px

.safari-itineraries__timing
  position: relative
  display: flex
  align-items: flex-start
  gap: 5px
  margin-bottom: 6px

  &:hover .safari-itineraries__remove-time:not(.safari-itineraries__remove-time--hide)
    opacity: 1

.safari-itineraries__timing--fake
  background-color: #fff8f0

  .safari-itineraries__description
    font-style: italic

.safari-itineraries__time
  width: 50px
  padding: 0
  border: none
  font-family: $font-family-base
  font-size: 15px
  font-weight: 800
  background-color: transparent

.safari-itineraries__description
  width: 100%
  height: auto
  padding: 0
  border: none
  font-family: $font-family-base
  font-size: 14px
  resize: none
  overflow: hidden
  background-color: transparent

.safari-itineraries__add-time
  line-height: 0

.safari-itineraries__remove-time
  background: none
  border: none
  font-size: 14px
  color: #f00
  cursor: pointer
  opacity: 0
  line-height: 16px
  transition: opacity 0.3s

.safari-itineraries__remove-time--hide
  pointer-events: none

.safari-itineraries__note
  width: 100%
  height: auto
  border: none
  font-family: $font-family-base
  resize: none
  overflow: hidden

.safari-itineraries__meal-warning
  padding: 8px 10px
  margin-bottom: 10px
  background-color: #fff3e8
  border-left: 3px solid #e85c20
  font-size: 14px
</style>
